import type { QualitySettings } from "../perf/QualityManager";

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicDuck: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  private freqData: Uint8Array = new Uint8Array(0);
  private fftSize: number;
  private masterVolume = 0.7;
  private started = false;

  constructor(quality: QualitySettings) {
    this.fftSize = quality.audioFftSize;
  }

  ensureContext(): AudioContext {
    if (!this.ctx) {
      const Ctx =
        (window.AudioContext as typeof AudioContext) ||
        (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) {
        throw new Error("Web Audio is not supported in this browser");
      }
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.masterVolume;
      // Side-chain bus for music: ducked on every kick so the rest of the
      // mix breathes around the beat. Anything that should NOT duck (UI
      // sfx, voice) connects to `destination` (= master) directly.
      this.musicDuck = this.ctx.createGain();
      this.musicDuck.gain.value = 1;
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = this.fftSize;
      this.analyser.smoothingTimeConstant = 0.7;
      this.freqData = new Uint8Array(this.analyser.frequencyBinCount);
      this.musicDuck.connect(this.master);
      this.master.connect(this.analyser);
      this.analyser.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  get destination(): AudioNode | null {
    return this.master;
  }

  get musicDestination(): AudioNode | null {
    return this.musicDuck;
  }

  scheduleDuck(time: number, depth = 0.5, attackMs = 8, releaseMs = 220): void {
    if (!this.ctx || !this.musicDuck) return;
    const g = this.musicDuck.gain;
    const t0 = Math.max(time, this.ctx.currentTime);
    g.cancelScheduledValues(t0);
    g.setValueAtTime(1, t0);
    g.linearRampToValueAtTime(depth, t0 + attackMs / 1000);
    g.linearRampToValueAtTime(1, t0 + (attackMs + releaseMs) / 1000);
  }

  setMasterVolume(value: number): void {
    this.masterVolume = Math.max(0, Math.min(1, value));
    if (this.master) this.master.gain.value = this.masterVolume;
  }

  async start(): Promise<void> {
    if (this.started) return;
    const ctx = this.ensureContext();
    if (ctx.state === "suspended") await ctx.resume();
    this.started = true;
  }

  suspend(): void {
    if (this.ctx && this.ctx.state === "running") {
      void this.ctx.suspend();
    }
  }

  resume(): void {
    if (this.ctx && this.ctx.state === "suspended") {
      void this.ctx.resume();
    }
  }

  private sampleFreq(): void {
    if (!this.analyser) return;
    this.analyser.getByteFrequencyData(this.freqData as Uint8Array<ArrayBuffer>);
  }

  private bandEnergy(fromBin: number, toBin: number): number {
    if (!this.analyser) return 0;
    this.sampleFreq();
    let sum = 0;
    let count = 0;
    const last = Math.min(this.freqData.length, toBin);
    for (let i = fromBin; i < last; i++) {
      sum += this.freqData[i]!;
      count++;
    }
    if (count === 0) return 0;
    return sum / (count * 255);
  }

  lowBandEnergy(): number {
    if (!this.analyser) return 0;
    const bins = this.analyser.frequencyBinCount;
    return this.bandEnergy(0, Math.max(2, Math.floor(bins * 0.1)));
  }

  midBandEnergy(): number {
    if (!this.analyser) return 0;
    const bins = this.analyser.frequencyBinCount;
    return this.bandEnergy(Math.floor(bins * 0.1), Math.floor(bins * 0.5));
  }

  highBandEnergy(): number {
    if (!this.analyser) return 0;
    const bins = this.analyser.frequencyBinCount;
    return this.bandEnergy(Math.floor(bins * 0.5), bins);
  }

  dispose(): void {
    if (this.ctx) {
      void this.ctx.close();
    }
    this.ctx = null;
    this.master = null;
    this.musicDuck = null;
    this.analyser = null;
  }
}
